// src/helpers/adminSecurityService.js
import { get, post } from './apiHelper';

const adminSecurityService = {
    /**
     * Bloquea una IP manualmente (temporal o permanente)
     * @param {string} ip - Dirección IP a bloquear
     * @param {string} reason - Razón del bloqueo
     * @param {number} durationHours - Duración en horas (opcional, default: 24)
     * @param {boolean} isPermanent - Si es permanente (opcional, default: false)
     */
    blockIP: async (ip, reason, durationHours = 24, isPermanent = false) => {
        try {
            return await post('admin/security/block-ip', {
                ip,
                reason,
                durationHours,
                isPermanent,
            });
        } catch (error) {
            console.error('Error blocking IP:', error);
            throw error;
        }
    },

    /**
     * Desbloquea una IP previamente bloqueada
     * @param {string} ip - Dirección IP a desbloquear
     */
    unblockIP: async (ip) => {
        try {
            return await post('admin/security/unblock-ip', { ip });
        } catch (error) {
            console.error('Error unblocking IP:', error);
            throw error;
        }
    },

    /**
     * Obtiene todas las IPs bloqueadas (temporales y permanentes)
     */
    getBlockedIPs: async () => {
        try {
            return await get('admin/security/blocked-ips');
        } catch (error) {
            console.error('Error fetching blocked IPs:', error);
            throw error;
        }
    },

    /**
     * Obtiene estadísticas detalladas de una IP específica
     * @param {string} ip - Dirección IP
     */
    getIPStats: async (ip) => {
        try {
            return await get(`admin/security/ip-stats/${encodeURIComponent(ip)}`);
        } catch (error) {
            console.error('Error fetching IP stats:', error);
            throw error;
        }
    },

    /**
     * Busca y elimina clientes que coincidan con un patrón
     * @param {string} pattern - Patrón a buscar (regex compatible)
     * @param {Object} options - Opciones de búsqueda
     * @param {boolean} options.searchInNames - Buscar en nombres (default: true)
     * @param {boolean} options.searchInEmails - Buscar en emails (default: false)
     * @param {string} options.marcaId - Filtrar por marca específica (opcional)
     * @param {boolean} options.dryRun - Solo mostrar resultados sin eliminar (default: false)
     */
    deleteClientsByPattern: async (pattern, options = {}) => {
        try {
            const {
                searchInNames = true,
                searchInEmails = false,
                marcaId = null,
                dryRun = false,
            } = options;

            return await post('admin/security/delete-clients-by-pattern', {
                pattern,
                searchInNames,
                searchInEmails,
                marcaId,
                dryRun,
            });
        } catch (error) {
            console.error('Error deleting clients by pattern:', error);
            throw error;
        }
    },

    /**
     * Obtiene estadísticas generales de seguridad
     * @param {Object} options - Opciones de filtrado
     * @param {string} options.marcaId - Filtrar por marca específica (opcional)
     * @param {number} options.days - Días hacia atrás (default: 7, max: 90)
     */
    getSecurityStats: async (options = {}) => {
        try {
            const { marcaId = null, days = 7 } = options;

            // Asegurar que days sea un número válido entre 1 y 90
            // Nota: Los query params siempre se envían como strings en HTTP
            // El backend debe usar ParseIntPipe o @Type(() => Number) para parsearlos
            const daysNumber = typeof days === 'number' ? days : parseInt(days, 10);
            const validDays = isNaN(daysNumber) || daysNumber < 1 || daysNumber > 90
                ? 7  // Valor por defecto válido
                : Math.max(1, Math.min(90, daysNumber));

            // Construir query string manualmente para asegurar formato correcto
            const queryParts = [];
            queryParts.push(`days=${validDays}`); // Siempre incluir days con valor válido

            if (marcaId) {
                queryParts.push(`marcaId=${encodeURIComponent(marcaId)}`);
            }

            const queryString = queryParts.join('&');
            const url = `admin/security/stats?${queryString}`;

            return await get(url);
        } catch (error) {
            console.error('Error fetching security stats:', error);
            // Si el error es sobre days, proporcionar información útil
            if (error.message && error.message.includes('days')) {
                console.warn(
                    'El backend necesita parsear el parámetro "days" como número. ' +
                    'Asegúrate de usar ParseIntPipe o @Type(() => Number) en el DTO del backend.'
                );
            }
            throw error;
        }
    },
};

export default adminSecurityService;

